/**
 * useKeyboardShortcuts.js — Section 11
 *
 * Global keyboard shortcuts:
 *
 *  1. Space           → Play / Pause toggle
 *  2. Cmd/Ctrl + Z    → Undo
 *  3. Cmd/Ctrl+Shift+Z → Redo
 *  4. Cmd/Ctrl + C    → Copy selected clip(s)
 *  5. Cmd/Ctrl + V    → Paste at playhead position
 *  6. Cmd/Ctrl + D    → Duplicate selected clip
 *  7. Delete/Backspace → Delete selected clip(s)
 *  8. S               → Toggle Slice tool
 *  9. V               → Switch to Select tool
 * 10. Cmd/Ctrl + S    → Save project
 * 11. Cmd/Ctrl + E    → Open Export modal
 * 12. 1-9             → Jump to bar (1=bar 1, 9=bar 9)
 * 13. Left/Right Arrow → Move playhead by 1 bar
 * 14. Shift+Left/Right → Move playhead by quarter note (0.25 bar)
 * 15. Cmd/Ctrl+Scroll  → Horizontal zoom
 * 16. R                → Record
 * 17. Cmd/Ctrl+Shift+D → Duplicate selected track
 * 18. Shift+Up/Down   → Transpose selected loops a semitone
 */

import { useEffect } from 'react';

/**
 * @param {Object} handlers - Callback map:
 *   onPlayPause()
 *   onUndo()
 *   onRedo()
 *   onCopy()
 *   onPaste()
 *   onDuplicate()
 *   onDelete()
 *   onSliceTool()
 *   onSelectTool()
 *   onSave()
 *   onExport()
 *   onJumpToBar(barNumber)        — 1-indexed (1–9)
 *   onMovePlayhead(deltaBar)      — +1, -1, +0.25, -0.25
 */
export function useKeyboardShortcuts(handlers) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.defaultPrevented || e.isComposing) return;
      const isMod = e.metaKey || e.ctrlKey;
      const isShift = e.shiftKey;
      const key = e.key;
      const code = e.code;

      // Ignore if user is typing in an input/textarea
      const tag = e.target.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;

      if (key === 'Enter' && !isMod && !isShift && !e.altKey) {
        const editing = isInput || e.target.closest?.('[contenteditable]:not([contenteditable="false"])') || tag === 'SELECT' || e.target.closest?.('dialog,[role="dialog"],[role="alertdialog"],[aria-modal="true"],.ba-nav-popover,.ba-nav-menu');
        if (editing || e.repeat || !handlers.onReturnToStart) return;
        e.preventDefault(); handlers.onReturnToStart(); return;
      }

      // --- Mod shortcuts (always active, even in inputs for save/undo) ---

      // 2. Cmd+Z → Undo
      if (isMod && !isShift && (key === 'z' || key === 'Z')) {
        e.preventDefault();
        handlers.onUndo?.();
        return;
      }

      // 3. Cmd+Shift+Z → Redo
      if (isMod && isShift && (key === 'z' || key === 'Z')) {
        e.preventDefault();
        handlers.onRedo?.();
        return;
      }

      // 10. Cmd+S → Save
      if (isMod && !isShift && (key === 's' || key === 'S')) {
        e.preventDefault();
        handlers.onSave?.();
        return;
      }

      // 11. Cmd+E → Export modal
      if (isMod && !isShift && (key === 'e' || key === 'E')) {
        e.preventDefault();
        handlers.onExport?.();
        return;
      }

      // --- Remaining shortcuts only when NOT in an input ---
      if (isInput) return;

      if (isMod && !isShift && (key === 'a' || key === 'A')) {
        e.preventDefault();
        handlers.onSelectAll?.();
        return;
      }

      // 4. Cmd+C → Copy
      if (isMod && !isShift && (key === 'c' || key === 'C')) {
        e.preventDefault();
        handlers.onCopy?.();
        return;
      }

      // 5. Cmd+V → Paste
      if (isMod && !isShift && (key === 'v' || key === 'V')) {
        e.preventDefault();
        handlers.onPaste?.();
        return;
      }

      // 6. Cmd+D → Duplicate
      if (isMod && isShift && (key === 'd' || key === 'D')) {
        e.preventDefault();
        handlers.onDuplicateTrack?.();
        return;
      }

      if (isMod && !isShift && (key === 'd' || key === 'D')) {
        e.preventDefault();
        handlers.onDuplicate?.();
        return;
      }

      // 7. Delete/Backspace → Delete selected
      if (key === 'Delete' || key === 'Backspace') {
        e.preventDefault();
        handlers.onDelete?.();
        return;
      }

      // 1. Space → Play/Pause
      if (code === 'Space') {
        e.preventDefault();
        handlers.onPlayPause?.();
        return;
      }

      if (!isMod && !isShift && (key === 'r' || key === 'R')) {
        e.preventDefault();
        handlers.onRecord?.();
        return;
      }

      if (key === 'Escape') {
        handlers.onEscape?.();
        return;
      }

      // The dock owns letter keys, while transport and modifier chords remain global.
      if (handlers.suspendLetters && !isMod && /^[a-z]$/i.test(key)) return;

      if (!isMod && !isShift && (key === 'l' || key === 'L')) {
        handlers.onToggleLoop?.();
        return;
      }

      if (!isMod && key === '?') {
        handlers.onShortcuts?.();
        return;
      }

      // 8. S → Slice tool (only bare key, not Cmd+S)
      if (!isMod && (key === 's' || key === 'S') && !isShift) {
        handlers.onSliceTool?.();
        return;
      }

      // 9. V → Select tool (only bare key, not Cmd+V)
      if (!isMod && (key === 'v' || key === 'V') && !isShift) {
        handlers.onSelectTool?.();
        return;
      }

      // 12. 1-9 → Jump to bar
      if (!isMod && !isShift && key >= '1' && key <= '9') {
        const barNumber = parseInt(key, 10);
        handlers.onJumpToBar?.(barNumber);
        return;
      }

      // 18. Shift+Up/Down → Transpose the picked loops a semitone
      if (isShift && !isMod && (key === 'ArrowUp' || key === 'ArrowDown')) {
        e.preventDefault();
        handlers.onTranspose?.(key === 'ArrowUp' ? 1 : -1);
        return;
      }

      // 13. Left/Right → Move playhead by 1 bar
      // 14. Shift+Left/Right → Move playhead by quarter note
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        e.preventDefault();
        const direction = key === 'ArrowRight' ? 1 : -1;
        const delta = isShift ? direction * 0.25 : direction * 1;
        handlers.onMovePlayhead?.(delta);
        return;
      }
    }

    // 15. Cmd+Scroll → Zoom
    function handleWheel(e) {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        handlers.onZoom?.(e.deltaY < 0 ? 1 : -1);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [handlers]);
}

export default useKeyboardShortcuts;
